/**
 * The sign-in pages' frame (Login and Convite), in the public site's design: the landing page's
 * dark, glowing brand surface beside a light form surface, so whoever clicks "Entrar" on the site
 * lands on the same product.
 *
 * Desktop: the brand panel on the left (the product's promise playing on the phone, the three
 * trust lines, the privacy line), the form on the right. Phones: no pitch to scroll past — a short
 * dark band with the logo, and the form as a light sheet riding up over it.
 */
import { useEffect, type ReactNode } from "react";
import { ArrowLeft, CalendarCheck, QrCode, ShieldCheck } from "lucide-react";
import { Logo } from "@client/components/Logo";
import { PhoneLoop } from "@client/pages/landing/mocks";
import { useInView } from "@client/pages/landing/ui";
import "@client/styles/site.css";

const TRUST = [
  { icon: QrCode, text: "Seu número de sempre, pelo QR code" },
  { icon: ShieldCheck, text: "Nada sai sem a sua aprovação" },
  { icon: CalendarCheck, text: "Integrado ao Google Agenda" },
];

export function AuthShell({ eyebrow, headline, tagline, children, below }: {
  /** The brand panel's small label and headline (desktop), and the phone band's one line. */
  eyebrow: string; headline: ReactNode; tagline: string;
  /** The card's content. */
  children: ReactNode;
  /** Under the card: links that are not part of the form. */
  below?: ReactNode;
}) {
  const phone = useInView<HTMLDivElement>({ once: false, margin: "0px" });

  useEffect(() => {
    // Same reason as the landing: the document behind is the app's light background, and the
    // dark band must not bounce into white on an overscroll.
    const root = document.documentElement;
    const before = root.style.backgroundColor;
    root.style.backgroundColor = "#09141d";
    return () => { root.style.backgroundColor = before; };
  }, []);

  return (
    <div className="site js-ready flex min-h-dvh flex-col antialiased lg:grid lg:grid-cols-[1.05fr_1fr]">
      {/* ── Brand panel: a short band on phones, the full panel on desktop ── */}
      <section className="relative overflow-hidden px-6 pb-12 pt-6 lg:flex lg:min-h-dvh lg:flex-col lg:justify-between lg:p-12">
        <div className="l-dots pointer-events-none absolute inset-0" />
        <div className="l-hero-glow pointer-events-none absolute inset-0" />

        <div className="relative flex items-center justify-between">
          <a href="/" aria-label="ChatNutri, voltar para o site"><Logo size="lg" variant="light" /></a>
          <a href="/" className="flex items-center gap-1.5 text-sm font-semibold text-[var(--l-muted)] transition-colors hover:text-white">
            <ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">Voltar para o site</span><span className="sm:hidden">Site</span>
          </a>
        </div>

        <p className="relative mt-6 text-sm text-[var(--l-muted)] lg:hidden">{tagline}</p>

        <div className="relative hidden lg:block">
          <p className="l-eyebrow mb-4">{eyebrow}</p>
          <h2 className="l-h2 mb-8 max-w-md text-white" style={{ fontSize: "clamp(2rem, 3vw, 2.75rem)" }}>{headline}</h2>
          <div className="grid items-center gap-10 xl:grid-cols-[auto_1fr]">
            <div ref={phone.ref} className="relative [@media(max-height:760px)]:hidden">
              <div className="l-stage pointer-events-none absolute -inset-12" />
              <PhoneLoop playing={phone.inView} size="md" />
            </div>
            <ul className="space-y-4 [@media(min-height:761px)]:hidden xl:[@media(min-height:761px)]:block">
              {TRUST.map((t) => (
                <li key={t.text} className="flex items-center gap-3 text-sm font-medium text-[var(--l-text)]">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[var(--l-accent)]/10">
                    <t.icon className="h-4 w-4 text-[var(--l-accent)]" />
                  </span>
                  {t.text}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <p className="relative hidden max-w-md text-xs text-[var(--l-muted)] lg:block">
          Seus dados e os dos seus pacientes ficam na sua conta, separados de qualquer outra.
        </p>
      </section>

      {/* ── Form surface ── */}
      <section className="l-light l-sheet relative flex flex-1 items-start justify-center px-5 pb-12 pt-8 lg:min-h-dvh lg:items-center lg:px-10">
        <div className="w-full max-w-[26rem]">
          <div className="l-card rounded-3xl p-6 sm:p-8">{children}</div>
          {below && <div className="mt-6 space-y-2 text-center text-sm text-[var(--l-ink-muted)]">{below}</div>}
          <p className="mt-8 text-center text-xs text-[var(--l-ink-muted)] lg:hidden">
            Seus dados e os dos seus pacientes ficam na sua conta, separados de qualquer outra.
          </p>
        </div>
      </section>
    </div>
  );
}
